import { defineStore } from 'pinia'
import { ref } from 'vue'
import { FEATURE_FLAGS } from '../../feature-flag'
import { appSessionId } from '../../http-client'
import { globalUtils } from '@gat-ship/utils'

export interface SignalRReceiveEvent<T> {
  /** The unique primary id. */
  messageId: string
  /** A unique readable string id. Can be used to create discriminators and is typically the event-name. */
  messageType: string
  /** The event-category  */
  category: string
  /** The payload. Can be anything */
  message: T
  /** ISO DateTime string with Timezone. */
  sentAt: string
  /** Typically the commandId returned from backend.  */
  correlationId: string
  /** The current session ID. Will be unique for every browser-tab/session. */
  appSessionId: string
  /** is a tag-based id which indicates {originator}:{id}. Example user:668a072d-22db-44e2-9f1b-1a3d91bd68d1 */
  originator: string
}

export type ActiveSubscriptionFilter = (...payload: any) => boolean

export interface ActiveSignalRSubscription {
  id: string
  eventName: string
  cb: (...payload: any) => void
  filters: ActiveSubscriptionFilter[]
}

export interface SubscribeToEventOptions {
  /** Is default false. Will not trigger the callback on any events which originates from this user(self). */
  recieveOwnEvents?: boolean
}

export function useSignalRSubscriptions() {
  /** Holds all currently active subscriptions. */
  const activeSubscriptions = ref<ActiveSignalRSubscription[]>([])

  /** Adds a event-listener to the given event name. The callback is executed whenever the event is recieved. */
  function addSubscription(
    id: string,
    eventName: string,
    cb: (...palyoad: any) => void,
    filters: ActiveSubscriptionFilter[] = []
  ) {
    activeSubscriptions.value.push({ id, eventName, cb, filters })
    if (FEATURE_FLAGS.IS_DEV) {
      console.log(
        `%c signalR - subscribed to event: ${eventName}`,
        'background: #222; color: #bada55'
      )
    }
  }

  /** Removes the most recent subscription with the given name. */
  function removeSubscription(id: string) {
    const idx = activeSubscriptions.value.findIndex((i) => i.id === id)
    if (idx >= 0) {
      const subscription = activeSubscriptions.value[idx]
      activeSubscriptions.value.splice(idx, 1)
      if (FEATURE_FLAGS.IS_DEV) {
        console.log(
          `%c signalR - unsubscribed to event: ${subscription.eventName}`,
          'background: #222; color:rgb(218, 123, 85)'
        )
      }
    }
  }

  /**
   * Subscribes to a SignalR event.
   * @param {string} messageType - The event name to subscribe to.
   * @param {Function} cb - The callback function to trigger on event.
   * @param {SubscribeToEventOptions} [options] - Options for the event subscription.
   * @returns {Function} Callable unsubscribe function.
   */
  function subscribeToEvent<T>(
    messageType: string,
    cb: (payload: SignalRReceiveEvent<T>) => void,
    options?: SubscribeToEventOptions
  ) {
    const filters: ActiveSubscriptionFilter[] = []

    // Only add the filter if recieveOwnEvents is not explicitly set to true
    if (!options?.recieveOwnEvents) {
      const isSentFromDifferentTabOrDevice = (
        payload: SignalRReceiveEvent<T>
      ) => {
        if (!payload.appSessionId) {
          return false
        }
        return payload.appSessionId !== appSessionId
      }
      filters.push(isSentFromDifferentTabOrDevice)
    }

    const id = globalUtils.functions.uuid()
    addSubscription(id, messageType, cb, filters)

    return () => removeSubscription(id)
  }

  return {
    activeSubscriptions,
    subscribeToEvent
  }
}

const debug = true

export const useSignalRStore = defineStore('signalR-store', () => {
  const { activeSubscriptions, subscribeToEvent } = useSignalRSubscriptions()

  /** Holds all recieved events. Only works in DEV/staging mode. */
  const recievedEvents = ref<SignalRReceiveEvent<unknown>[]>([])

  /** Called by the signalR service whenever an event has been recieved. */
  function onEventRecieved(payload: SignalRReceiveEvent<unknown>) {
    if (debug) {
      console.log(
        `%c signalR - Event Recieved: ${payload.messageType ?? ''} \r\n payload:`,
        'background: #222; color: #bada55',
        payload
      )
    }
    const messageType = payload.messageType
    const subscriptions = activeSubscriptions.value.filter(
      (i) => i.eventName === messageType
    )
    if (
      (FEATURE_FLAGS.IS_DEV || FEATURE_FLAGS.IS_STAGING) &&
      subscriptions.length === 0
    ) {
      console.warn('No subscriptions found for event:', messageType)
    }
    for (let i = 0; i < subscriptions.length; i++) {
      const subscription = subscriptions[i]
      try {
        // Check if any filter returns false (meaning we should skip this event)
        const shouldSkip = subscription.filters.some(
          (filter) => filter(payload) === false
        )
        if (shouldSkip) {
          continue
        }
        subscription.cb(payload)
      } catch (error) {
        console.error('Error in SignalR subscription callback:', error)
        // Don't throw a generic error, just log it
      }
    }
    recievedEvents.value.push(payload)
  }

  return {
    onEventRecieved,
    subscribeToEvent,
    activeSubscriptions,
    recieveEvents: recievedEvents
  }
})
